<script setup>
import { reactive, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  batchId: { type: [String, Number], required: true },
});

const emit = defineEmits(['close', 'success']);
const store = useStore();
const { t } = useI18n();

const pipelines = useMapGetter('crmPipelines/getPipelines');
const lossReasons = useMapGetter('crmLossReasons/getLossReasons');
const uiFlags = useMapGetter('campaignBatches/getUIFlags');

const isPopulating = computed(() => uiFlags.value.isPopulating);

const filters = reactive({
  pipelineId: null,
  stageIds: [],
  status: null,
  lossReasonIds: [],
  userId: null,
  search: '',
  createdAfter: null,
  createdBefore: null,
});

const stages = ref([]);

const pipelineOptions = computed(() =>
  (pipelines.value || []).map(p => ({ value: p.id, label: p.name }))
);

const stageOptions = computed(() =>
  stages.value.map(s => ({ value: s.id, label: s.name }))
);

const statusOptions = [
  { value: 'open', label: 'Aberto' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

const isLostSelected = computed(() => filters.status === 'lost');

const lossReasonOptions = computed(() =>
  (lossReasons.value || []).map(r => ({ value: r.id, label: r.title }))
);

// When pipeline changes, load its stages
const onPipelineChange = async () => {
  filters.stageIds = [];
  if (!filters.pipelineId) {
    stages.value = [];
    return;
  }
  const pipeline = (pipelines.value || []).find(
    p => p.id === filters.pipelineId
  );
  stages.value = pipeline?.stages || [];
};

onMounted(() => {
  store.dispatch('crmPipelines/get');
  store.dispatch('crmLossReasons/get');
});

const handleImport = async () => {
  const payload = {};
  if (filters.pipelineId) payload.pipeline_id = filters.pipelineId;
  if (filters.stageIds.length) payload.stage_ids = filters.stageIds;
  if (filters.status) payload.status = filters.status;
  if (filters.lossReasonIds.length) payload.loss_reason_ids = filters.lossReasonIds;
  if (filters.userId) payload.user_id = filters.userId;
  if (filters.search) payload.search = filters.search;
  if (filters.createdAfter) payload.created_after = filters.createdAfter;
  if (filters.createdBefore) payload.created_before = filters.createdBefore;

  try {
    const result = await store.dispatch('campaignBatches/populateFromDeals', {
      batchId: props.batchId,
      filters: payload,
    });
    useAlert(
      t('CAMPAIGN.BATCH.CRM_IMPORT.SUCCESS', {
        added: result.added_count,
        skipped: result.skipped_count,
      })
    );
    emit('success', result);
  } catch (e) {
    useAlert(t('CAMPAIGN.BATCH.CRM_IMPORT.ERROR'));
  }
};
</script>

<template>
  <div class="fixed inset-0 bg-n-alpha-black2 z-50 flex items-center justify-center p-4" @click.self="emit('close')">
    <div
      class="w-full max-w-lg bg-n-solid-2 backdrop-blur-[100px] rounded-xl border border-n-weak shadow-lg max-h-[85vh] overflow-y-auto">
      <div class="p-6 flex flex-col gap-5">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.BATCH.CRM_IMPORT.TITLE') }}
          </h3>
          <Button variant="ghost" color="slate" size="sm" icon="i-lucide-x" @click="emit('close')" />
        </div>

        <p class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.BATCH.CRM_IMPORT.DESCRIPTION') }}
        </p>

        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.BATCH.CRM_IMPORT.PIPELINE') }}
            </label>
            <ComboBox v-model="filters.pipelineId" :options="pipelineOptions"
              :placeholder="t('CAMPAIGN.BATCH.CRM_IMPORT.PIPELINE_PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2" @update:model-value="onPipelineChange" />
          </div>

          <div v-if="stageOptions.length" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.BATCH.CRM_IMPORT.STAGE') }}
            </label>
            <TagMultiSelectComboBox v-model="filters.stageIds" :options="stageOptions"
              :placeholder="t('CAMPAIGN.BATCH.CRM_IMPORT.STAGE_PLACEHOLDER')" />
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.BATCH.CRM_IMPORT.STATUS') }}
            </label>
            <ComboBox v-model="filters.status" :options="statusOptions"
              :placeholder="t('CAMPAIGN.BATCH.CRM_IMPORT.STATUS_PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2" />
          </div>

          <div v-if="isLostSelected" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.BATCH.CRM_IMPORT.LOSS_REASON') }}
            </label>
            <TagMultiSelectComboBox v-model="filters.lossReasonIds" :options="lossReasonOptions"
              :placeholder="t('CAMPAIGN.BATCH.CRM_IMPORT.LOSS_REASON_PLACEHOLDER')" />
          </div>

          <Input v-model="filters.search" :label="t('CAMPAIGN.BATCH.CRM_IMPORT.SEARCH')"
            :placeholder="t('CAMPAIGN.BATCH.CRM_IMPORT.SEARCH_PLACEHOLDER')" />

          <div class="grid grid-cols-2 gap-3">
            <Input v-model="filters.createdAfter" :label="t('CAMPAIGN.BATCH.CRM_IMPORT.DATE_FROM')" type="date" />
            <Input v-model="filters.createdBefore" :label="t('CAMPAIGN.BATCH.CRM_IMPORT.DATE_TO')" type="date" />
          </div>
        </div>

        <div class="flex gap-3 justify-between items-center w-full pt-2">
          <Button variant="faded" color="slate" type="button" :label="t('CAMPAIGN.BATCH.CRM_IMPORT.CANCEL')"
            class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3" @click="emit('close')" />
          <Button :label="t('CAMPAIGN.BATCH.CRM_IMPORT.IMPORT')" class="w-full" icon="i-lucide-download"
            :is-loading="isPopulating" :disabled="isPopulating || !filters.pipelineId" @click="handleImport" />
        </div>
      </div>
    </div>
  </div>
</template>
